import { deepmerge } from '@fastify/deepmerge';
import indexArray from 'index-array';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _set from 'lodash/set';

import { formatCurrency } from '@atc/string-fns';

import {
    buildVDPBaseUrl,
    getFourthSpotlightTier,
    listingDataPreprocessing,
} from '@/utilities/dispatchUtils';
import processPaymentServices from '@/utilities/processPaymentServices';
import { createSummaryContent } from '@/utilities/summaryContentUtilities';

import inventoryDuck from '@/ducks/inventoryDuck.js';
import ownersDuck from '@/ducks/ownersDuck.js';
import requestParamsDuck from '@/ducks/requestParamsDuck.js';
import srpAdsDuck from '@/ducks/srp/srpAdsDuck.js';
import srpContentDuck from '@/ducks/srp/srpContentDuck.js';
import srpEditorialReviewsDuck from '@/ducks/srp/srpEditorialReviewsDuck.js';
import srpExpiredListingMessageDuck from '@/ducks/srp/srpExpiredListingMessageDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpMarketExtensionDuck from '@/ducks/srp/srpMarketExtensionDuck.js';
import srpNewCarBoostDuck from '@/ducks/srp/srpNewCarBoostDuck.js';
import srpNewSearchDuck from '@/ducks/srp/srpNewSearchDuck.js';
import srpNoSearchResultsDuck from '@/ducks/srp/srpNoSearchResultsDuck.js';
import srpPaginationDuck from '@/ducks/srp/srpPaginationDuck.js';
import srpPrimeSpotlightDuck from '@/ducks/srp/srpPrimeSpotlightDuck.js';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck.js';
import srpReuseActiveFourthSpotlightDuck from '@/ducks/srp/srpReuseActiveFourthSpotlightDuck.js';
import srpReuseActiveStateDuck from '@/ducks/srp/srpReuseActiveStateDuck.js';
import srpSortDuck from '@/ducks/srp/srpSortDuck.js';
import srpSponsorshipDuck from '@/ducks/srp/srpSponsorshipDuck.js';
import srpSpotlightDuck from '@/ducks/srp/srpSpotlightDuck.js';
import srpStrongAttributesDuck from '@/ducks/srp/srpStrongAttributesDuck.js';
import srpSuggestedFiltersDuck from '@/ducks/srp/srpSuggestedFiltersDuck.js';
import srpSuggestedMakeModelValuesDuck from '@/ducks/srp/srpSuggestedMakeModelValuesDuck.js';
import userAgentDuck from '@/ducks/userAgentDuck';
import userDuck from '@/ducks/userDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import buildSRPFilterGroupReferenceData from './srp/buildSRPFilterGroupReferenceData';
import buildSRPFiltersReferenceData from './srp/buildSRPFiltersReferenceData';

const overwriteMerge = () => (destinationArray, sourceArray) => sourceArray;

export default function dispatchSearchResultsPageData() {
    return async (ctx, next) => {
        const state = ctx.store.getState();

        const reuseActiveState = srpReuseActiveStateDuck.selectors.getDuckState(state);
        const isBot = userAgentDuck.selectors.isBot(state);
        const vdpUrl = await buildVDPBaseUrl(ctx);

        const {
            brand: [, brandVariables],
            enable_new_car_boost: [enableNewCarBoost],
            listing_card_ad: [enableListingCardAd],
            srp_inline_filters: [enableInlineFilters],
            srp_wallet_grid_promo_card: [enableWalletCard],
            standard_listing_phone_display: [displayPhoneNumber],
            standard_listing_owner_name: [hideOwnerName],
            nds_calc: [, { forceDefaultRates }],
            nds_lease: [isNdsLeaseEnabled, leaseEnabledDRTypes = {}],
            nds_price_breakdown: [isPriceBreakdownEnabled, priceBkdwnEnabledDRTypes = {}],
            non_dt_payment_services: [enabledNonDTPaymentServices],
            use_fba_payment_calc: [enableFBAPaymentCalc],
            spotlight: [isSpotlightEnabled],
            collapse_srp_filters_state: [enableCollapseFilters, { exclude_filters: excludeFilters = [] }],
            default_num_records: [, { num_records: defaultNumRecordsOptions }],
            premium_spotlight: [enablePremiumSpotlight],
            reference_filters: [showRefFilters],
            vehicle_highlight: [isVehicleHighlightEnabled],
        } = ctx.useFeatures([
            'brand',
            'enable_new_car_boost',
            'listing_card_ad',
            'srp_inline_filters',
            'standard_listing_phone_display',
            'standard_listing_owner_name',
            'nds_calc',
            'nds_lease',
            'nds_price_breakdown',
            'non_dt_payment_services',
            'srp_wallet_grid_promo_card',
            'spotlight',
            'use_fba_payment_calc',
            'collapse_srp_filters_state',
            'default_num_records',
            'premium_spotlight',
            'reference_filters',
            'vehicle_highlight',
        ]);

        const filters = showRefFilters ? await buildSRPFiltersReferenceData(ctx, false) : ctx?.data?.filters;
        const filterGroups = showRefFilters ? await buildSRPFilterGroupReferenceData(ctx, true) : ctx?.data?.filterGroups;

        ctx.batchDispatch.createActions((deferredCtx) => {
            const requestId = deferredCtx?.data?.requestParams?.requestId;

            // if page is not done loading before a new filter is selected, abort the current page load
            if (typeof window !== 'undefined' && requestId && window.sessionStorage.getItem('requestId')
                && requestId !== window.sessionStorage.getItem('requestId')) {

                next('abort queue');
            }

            const {
                _cookies,
                alpha,
                boost,
                editorialContent,
                editorialReviews,
                listings = [],
                owners = [],
                makeSummaryData,
                pageData = {},
                spotlights = [],
                vehicleHighlight = {},
                standardSpotlights = {},
                strongAttributesFilters = [],
                suggestedFilters = [],
                featuredSpotlights = {},
                stats,
                totalResultCount,
            } = deferredCtx.data;

            if (_cookies) {
                const searchLbCookie = _cookies.search_lbcookie;
                if (searchLbCookie && ctx.cookies && !isBot) {
                    ctx.cookies.set('search_lbcookie', searchLbCookie, { path: '/' });
                }
            }
            const _actions = [];

            if (!reuseActiveState) {

                const alphaListings = _get(alpha, 'alphaShowcase', []);
                const {
                    listings: spotlightListings = [],
                    owners: spotlightOwners = [],
                } = spotlights;
                const {
                    listings: [standardSpotlightListing] = [],
                    owners: [standardSpotlightOwner] = [],
                } = standardSpotlights;
                const {
                    listings: [featuredSpotlightListing] = [],
                    owners: [featuredSpotlightOwner] = [],
                } = featuredSpotlights;
                const {
                    listings: vehicleHighlightListings = [],
                    owners: vehicleHighlightOwners = [],
                } = vehicleHighlight;
                const boostListings = _get(boost, 'listings', []);

                // Get fourth spotlight listing if we have featured and/or standard listings in the listings results set
                const fourthSpotlightTier = getFourthSpotlightTier(listings);
                let fourthSpotlightListing;
                if (fourthSpotlightTier === 'Standard' && standardSpotlightListing && standardSpotlightOwner) {
                    fourthSpotlightListing = standardSpotlightListing;
                    spotlightOwners.push(standardSpotlightOwner);
                } else if (fourthSpotlightTier === 'Featured' && featuredSpotlightListing && featuredSpotlightOwner) {
                    fourthSpotlightListing = featuredSpotlightListing;
                    spotlightOwners.push(featuredSpotlightOwner);
                }

                // addSearchResultsPageData.js fetches 30 records when infinite scroll is active
                // if there are more listings than num records, and a special listing card is available
                // then remove the respective listing/fallback that would appear in that slot
                const isListingsMoreThanNumRecords = listings.length > (deferredCtx.query.numRecords
                    || defaultNumRecordsOptions?.value);

                if (isListingsMoreThanNumRecords) {
                    // 4 spotlights + wallet + premium spotlight + new car boost + inline filter
                    const maxRemovable = enableListingCardAd ? 9 : 8;
                    let removableCount = 0;

                    if (isSpotlightEnabled && spotlightListings.length > 0) {
                        for (let i = 0; i < spotlightListings.length; i += 1) {
                            removableCount += 1;
                        }
                    }
                    if (enableWalletCard) {
                        removableCount += 1;
                    }
                    if (enablePremiumSpotlight && isVehicleHighlightEnabled && vehicleHighlightListings.length > 0) {
                        removableCount += 1;
                    }
                    if (enableNewCarBoost && boostListings && boostListings.length > 0) {
                        removableCount += 1;
                    }
                    if (enableInlineFilters && listings.length >= 8) {
                        removableCount += 1;
                    }
                    if (enableListingCardAd && listings.length >= 25) {
                        removableCount += 1;
                    }
                    if (fourthSpotlightListing) {
                        removableCount += 1;
                    }

                    const numListingsToRemove = removableCount > maxRemovable ? maxRemovable : removableCount;

                    // remove listings from the end of the array
                    listings.splice(-numListingsToRemove, numListingsToRemove);
                }

                // aggregate all the listings from the page
                const aggregatedInventory = deepmerge({ all: true, mergeArray: overwriteMerge })(
                    // add the inventory from listings serverData
                    indexArray(listings, 'id'),

                    // add the spotlight listings if they exist
                    indexArray(spotlightListings, 'id'),

                    // add the vehicle highlight listings if they exist
                    indexArray(vehicleHighlightListings, 'id'),

                    // add the fourth spotlight listing if it exists
                    (fourthSpotlightListing ? { [fourthSpotlightListing.id]: fourthSpotlightListing } : {}),

                    // add alpha listing
                    indexArray(alphaListings, 'id'),

                    // add new car boost listings
                    indexArray(boostListings, 'id'),
                );

                const inventoryImageUrl = brandVariables.inventory_image_url;
                if (inventoryImageUrl && aggregatedInventory) {
                    Object.values(aggregatedInventory).forEach((listing) => {
                        const maxImageCount = listing.id === vehicleHighlightListings[0]?.id ? 4 : 1;
                        listingDataPreprocessing({
                            displayPhoneNumber,
                            hideOwnerName,
                            inventoryImageUrl,
                            listing,
                            owners,
                            vdpUrl,
                            maxImageCount,
                        });

                        // Process payment services
                        if (listing.paymentServices) {
                            processPaymentServices(listing, {
                                paymentDealerId: brandVariables.paymentDealerId,
                                paymentPartnerId: brandVariables.paymentPartnerId,
                                forceDefaultRates,
                                isNdsLeaseEnabled,
                                leaseEnabledDRTypes,
                                priceBkdwnEnabledDRTypes,
                                isPriceBreakdownEnabled,
                                enabledNonDTPaymentServices,
                                enableFBAPaymentCalc,
                            });
                        }
                    });

                    const vdpHasActiveResults = vdpResultsDuck.selectors.hasActiveResults(state);
                    const vdpActiveInventory = vdpHasActiveResults ? vdpResultsDuck.selectors.getVdpActiveInventory(state) : null;

                    if (vdpHasActiveResults && vdpActiveInventory?.id) {
                        const preservedInventory = {
                            ...aggregatedInventory,
                            [vdpActiveInventory.id]: vdpActiveInventory,
                        };
                        _actions.push(inventoryDuck.creators.setInventory(preservedInventory));
                    } else {
                        _actions.push(inventoryDuck.creators.setInventory(aggregatedInventory));
                    }
                }

                const defaultMetaDescription = brandVariables.default_meta_description;

                const summaryContentData = createSummaryContent(deferredCtx.data, deferredCtx.query);

                // aggregate all the owners
                const aggregatedOwners = deepmerge({ all: true, mergeArray: overwriteMerge })(
                    // add the owners from the owners serverData
                    indexArray(owners, 'id'),
                    indexArray(spotlightOwners, 'id'),
                    indexArray(vehicleHighlightOwners, 'id'),
                    indexArray(_get(boost, 'owners'), 'id'),
                    alpha?.ownerBean ? { [alpha.ownerBean.id]: alpha.ownerBean } : {},
                );
                // Reduce the amount of data that we dispatch/gets stored in __NEXT_DATA__
                Object.values(aggregatedOwners).forEach((owner) => {
                    if (owner.images?.sources?.length > 1) {
                        owner.images.sources.length = 1;
                    }
                });

                // Flag that there are any invalid query params
                const invalidQuery = deferredCtx.invalidQueryParams && deferredCtx.invalidQueryParams.length > 0;

                // flag for a no results found page
                const noResultsFound = totalResultCount === 0;

                pageData.summaryContentData = summaryContentData;
                pageData.makeSummaryData = makeSummaryData;
                pageData.editorialContent = editorialContent;
                pageData.defaultMetaDescription = defaultMetaDescription;

                const minPrice = stats?.derivedprice?.min;
                const maxPrice = stats?.derivedprice?.max;
                const minPricePlaceholder = minPrice && formatCurrency(minPrice);
                const maxPricePlaceholder = maxPrice && formatCurrency(maxPrice);
                // Set filters based on brand
                if (!_isEmpty(filters) && !_isEmpty(brandVariables?.filters)) {
                    Object.assign(filters, brandVariables?.filters);
                }

                const zipQuery = deferredCtx?.query?.zip;
                if (zipQuery && typeof zipQuery === 'string') {
                    const updatedZip = zipQuery.substring(0, 5);
                    if (!updatedZip.match(/^\d{5}$/)) {
                        delete deferredCtx.query.zip;
                    } else {
                        deferredCtx.query.zip = updatedZip;
                    }
                }

                // Add fourth spotlight listing to original list of spotlight listings at index 3 if it exists
                // IMPORTANT - We must explicitly add the fourth spotlight at index 3 in case there are fewer than
                // 3 spotlights in the original spotlights list
                const spotlightListingIds = spotlightListings.map((spotlight) => spotlight.id);
                if (fourthSpotlightListing?.id) {
                    spotlightListingIds[3] = fourthSpotlightListing?.id;
                }
                _actions.push(
                    // overwrite request params on each page change
                    requestParamsDuck.creators.setObject(deferredCtx.data.requestParams || {}),

                    // Process and set filters
                    // TODO: BONNET - optimizations?  Do we need to set the values if we already have them?
                    // perhaps we can query current state and check for filter values and ignore if they are already present?
                    srpFiltersDuck.creators.setSrpFilters({
                        filters,
                        filterGroups,
                        placeholders: {
                            minPrice: minPricePlaceholder,
                            maxPrice: maxPricePlaceholder,
                        },
                        query: deferredCtx.query,
                    }),

                    // Update the owners collection
                    ownersDuck.creators.setOwners(aggregatedOwners),

                    // Map the order to display results
                    srpResultsDuck.creators.setActiveResults(listings.map((listing) => listing.id)),

                    // Update the spotlight active results
                    srpSpotlightDuck.creators.setActiveResults(spotlightListingIds),

                    // Update the spotlight types
                    srpSpotlightDuck.creators.setSpotlightTypes({
                        premium: spotlightListings,
                        fourthTier: fourthSpotlightListing ? [fourthSpotlightListing] : null,
                    }, false),

                    // Update the vehicle highlight active results
                    srpPrimeSpotlightDuck.creators.setActiveResults(vehicleHighlightListings.map((listing) => listing.id)),

                    // Update the result count
                    srpResultsDuck.creators.setCount(invalidQuery ? 0 : totalResultCount),

                    // Update stats
                    srpResultsDuck.creators.setStats({
                        ...stats,
                        endYear: stats?.year?.max,
                        maxPrice,
                        minPrice,
                        startYear: stats?.year?.min,
                        searchRadius: deferredCtx.query.searchRadius,
                        zip: deferredCtx.query.zip,
                    }),

                    srpContentDuck.creators.setContent({ pageData }),

                    // TODO: BONNET - in page updates defaulted this to false.. do we need to recreate this somehow?
                    srpExpiredListingMessageDuck.creators.setKey('redirectExpiredPage', !!deferredCtx.req?.cookies?.redirectExpiredPage),

                    // Set market extension
                    srpMarketExtensionDuck.creators.setKeys({
                        active: _get(pageData, 'marketExtensionDma', true),
                        value: _get(deferredCtx, 'query.marketExtension', 'include'),
                        // TODO: BONNET - this was on filter change but not initial... weird
                        initializing: true,
                    }),

                    // Set the page's current page
                    srpPaginationDuck.creators.deriveCurrentPage({
                        firstRecord: deferredCtx.query.firstRecord,
                        numRecords: deferredCtx.query.numRecords || defaultNumRecordsOptions?.value,
                    }),

                    srpNoSearchResultsDuck.creators.setKeys({
                        showNoResults: !totalResultCount,
                    }),

                    srpSortDuck.creators.setSort(deferredCtx.query.sortBy || 'relevance'),
                    srpSponsorshipDuck.creators.setBoolean(deferredCtx.query.isSponsorship === 'true'),
                    srpSuggestedMakeModelValuesDuck.creators.setKeys(pageData.suggestedModels),

                    srpSuggestedFiltersDuck.creators.setKey('filters', suggestedFilters),
                    srpStrongAttributesDuck.creators.setKey('filters', strongAttributesFilters),
                );
                if (boostListings && boostListings.length > 0) {
                    _actions.push(srpNewCarBoostDuck.creators.setActiveResults(boostListings.map((listing) => listing.id)));
                }

                if (editorialReviews) {
                    if (Array.isArray(editorialReviews)) {
                        editorialReviews.forEach((review) => _set(review, 'guid.rendered', ''));
                    }

                    _actions.push(srpEditorialReviewsDuck.creators.set(editorialReviews));
                }

                // Update no results data
                if (noResultsFound || invalidQuery) {
                    _actions.push(srpNoSearchResultsDuck.creators.setKeys({
                        showNoResults: invalidQuery || noResultsFound,
                        message: 'Update filter criteria to see more results',
                    }));
                }
            }

            if (reuseActiveState) {
                _actions.push(
                    srpReuseActiveStateDuck.creators.setFalse(),
                    srpReuseActiveFourthSpotlightDuck.creators.setTrue(),
                );
            } else {
                _actions.push(srpReuseActiveFourthSpotlightDuck.creators.setFalse());
            }

            // Access isNewSearch directly from the Redux store
            const isNewSearch = srpNewSearchDuck.selectors.getDuckState(ctx.store.getState());
            _actions.push(

                // Set the page level targeting for Ads
                // TODO: BONNET - Should this not be getting passed to the Ads Provider?
                // Why is it in Redux?
                srpAdsDuck.creators.setPageLevelTargeting({
                    filters,
                    filterGroups,
                    query: deferredCtx.query,
                    // ...ads,
                    ...pageData.ad,
                }),

                // Flag that this is a new search, if no query param, assume it is a new search
                // TODO: BONNET - The fact that we are using query params to track makes me sad
                srpNewSearchDuck.creators.setBoolean(isNewSearch),

                // Save the current search
                userDuck.creators.setSavedSearchItem({
                    saveSearchString: _get(pageData, 'saveSearchData.myAtcSaveSearchString', ''),
                    textModalDisclaimer: _get(pageData, 'saveSearchData.textModalDisclaimer', ''),
                    title: _get(pageData, 'saveSearchData.bookmarkTitle', ''),
                    searchBookmarkDate: _get(pageData, 'saveSearchData.bookmarkSavedDateFormatted', ''),
                    saveSearchInstrText: _get(pageData, 'saveSearchData.saveSearchInstrText', ''),
                }),
            );

            if (enableCollapseFilters && typeof window === 'undefined') {
                // Set the collapse state of filters (server-side only)
                const excludeFiltersList = excludeFilters.split(',').map((filter) => filter.trim());
                _actions.push(srpFiltersDuck.creators.setCollapsedSrpFilters(excludeFiltersList));
            }

            return _actions;
        });

        // dispatch the batched action queue
        await ctx.batchDispatch.dispatchActions(ctx);

    };
}
